import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Trash2, Download, FileSpreadsheet, TrendingUp, TrendingDown,
  RefreshCw, IndianRupee, ChevronDown, CalendarDays, Clock
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNITS = [
  'gms', 'kg', 'ml', 'ltr', 'pcs', 'dozen',
  'cups', 'tbsp', 'tsp', 'bags', 'packets',
  'bottles', 'boxes', 'trays', 'nos'
];

const TODAY = new Date().toISOString().split('T')[0];
const CUR_YEAR = new Date().getFullYear();
const CUR_MONTH = new Date().getMonth() + 1;

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── Period Selector ─────────────────────────────────────────────────────────
const PeriodSelector = ({ period, setPeriod, custom, setCustom, monthYear, setMonthYear }) => (
  <div className="flex flex-wrap gap-2 items-center">
    {[
      { key: 'month', label: 'This Month' },
      { key: 'week',  label: 'This Week'  },
      { key: 'year',  label: 'This Year'  },
      { key: 'pick',  label: 'By Month'   },
      { key: 'custom',label: 'Custom'     },
    ].map(({ key, label }) => (
      <button
        key={key}
        onClick={() => setPeriod(key)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
          period === key
            ? 'bg-primary text-white shadow'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
        data-testid={`period-${key}`}
      >
        {label}
      </button>
    ))}
    {period === 'pick' && (
      <div className="flex items-center gap-2 mt-1 w-full sm:w-auto sm:mt-0">
        <Select value={String(monthYear.month)} onValueChange={v => setMonthYear(p => ({ ...p, month: Number(v) }))}>
          <SelectTrigger className="h-8 text-xs w-32">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          type="number" value={monthYear.year} min="2020" max={CUR_YEAR}
          onChange={e => setMonthYear(p => ({ ...p, year: Number(e.target.value) }))}
          className="h-8 text-xs w-24"
        />
      </div>
    )}
    {period === 'custom' && (
      <div className="flex items-center gap-2 mt-1 w-full sm:w-auto sm:mt-0">
        <Input type="date" value={custom.from} max={TODAY}
          onChange={e => setCustom(p => ({ ...p, from: e.target.value }))}
          className="h-8 text-xs w-36" />
        <span className="text-xs text-muted-foreground">to</span>
        <Input type="date" value={custom.to} max={TODAY}
          onChange={e => setCustom(p => ({ ...p, to: e.target.value }))}
          className="h-8 text-xs w-36" />
      </div>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminExpenses = ({ adminToken }) => {
  // Add-expense form
  const [form, setForm] = useState({ item_name: '', unit: 'gms', quantity: '', amount: '', date: TODAY, notes: '' });
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggRef = useRef(null);

  // Period / filter
  const [period, setPeriod] = useState('month');
  const [custom, setCustom] = useState({ from: TODAY, to: TODAY });
  const [monthYear, setMonthYear] = useState({ month: CUR_MONTH, year: CUR_YEAR });

  // Data
  const [expenses, setExpenses] = useState([]);
  const [expTotal, setExpTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  // Build query params from current period
  const periodParams = useCallback(() => {
    const p = new URLSearchParams();
    if (period === 'month')       { p.set('month', CUR_MONTH); p.set('year', CUR_YEAR); }
    else if (period === 'year')   { p.set('year', CUR_YEAR); }
    else if (period === 'week')   { p.set('week_of', TODAY); }
    else if (period === 'pick')   { p.set('month', monthYear.month); p.set('year', monthYear.year); }
    else if (period === 'custom' && custom.from && custom.to) {
      p.set('date_from', custom.from); p.set('date_to', custom.to);
    }
    return p;
  }, [period, custom, monthYear]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = periodParams();
      const [expRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/expenses?${p}`, { headers }),
        axios.get(`${API}/admin/accounts/summary?${p}`, { headers })
      ]);
      setExpenses(expRes.data.expenses);
      setExpTotal(expRes.data.total);
      setSummary(sumRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [periodParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggRef.current && !suggRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Autocomplete
  const fetchSuggestions = async (q) => {
    try {
      const res = await axios.get(`${API}/admin/expenses/suggestions?q=${encodeURIComponent(q)}`, { headers });
      setSuggestions(res.data);
      setShowSuggestions(res.data.length > 0);
    } catch {}
  };

  const handleItemNameChange = (val) => {
    setForm(p => ({ ...p, item_name: val }));
    if (val.trim().length >= 1) fetchSuggestions(val.trim());
    else { setSuggestions([]); setShowSuggestions(false); }
  };

  const selectSuggestion = (name) => {
    setForm(p => ({ ...p, item_name: name }));
    setShowSuggestions(false);
  };

  const handleAddExpense = async () => {
    if (!form.item_name.trim()) { toast.error('Item name is required'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.quantity || Number(form.quantity) <= 0) { toast.error('Enter a valid quantity'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/expenses`, {
        item_name: form.item_name.trim(),
        unit: form.unit,
        quantity: Number(form.quantity),
        amount: Number(form.amount),
        date: form.date,
        notes: form.notes
      }, { headers });
      toast.success('Expense added');
      setForm({ item_name: '', unit: 'gms', quantity: '', amount: '', date: TODAY, notes: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/expenses/${id}`, { headers });
      toast.success('Deleted');
      setExpenses(prev => prev.filter(e => e.id !== id));
      setExpTotal(prev => prev - (expenses.find(e => e.id === id)?.amount || 0));
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  // ─── Period label for exports ───────────────────────────────────────────────
  const periodLabel = () => {
    if (period === 'month') return `${MONTHS[CUR_MONTH - 1]} ${CUR_YEAR}`;
    if (period === 'year')  return `Year ${CUR_YEAR}`;
    if (period === 'week')  return `Week of ${TODAY}`;
    if (period === 'pick')  return `${MONTHS[monthYear.month - 1]} ${monthYear.year}`;
    return `${custom.from} to ${custom.to}`;
  };

  // ─── Export PDF ─────────────────────────────────────────────────────────────
  const exportPDF = (type) => {
    const doc = new jsPDF();
    const title = type === 'expenses'
      ? `Expense Report — ${periodLabel()}`
      : `Accounts Summary — ${periodLabel()}`;
    doc.setFontSize(14);
    doc.text('HHouse of Breadss', 14, 15);
    doc.setFontSize(11);
    doc.text(title, 14, 23);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, 29);
    doc.setTextColor(0);

    if (type === 'expenses') {
      autoTable(doc, {
        startY: 34,
        head: [['Date', 'Item', 'Qty', 'Unit', 'Amount (₹)', 'Notes']],
        body: expenses.map(e => [
          e.date, e.item_name, e.quantity, e.unit,
          Number(e.amount).toFixed(2), e.notes || ''
        ]),
        foot: [['', '', '', 'TOTAL', Number(expTotal).toFixed(2), '']],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [217, 119, 6] },
        footStyles: { fillColor: [254, 243, 199], textColor: [0, 0, 0], fontStyle: 'bold' }
      });
    } else {
      // Accounts sheet
      const rows = [
        ['Confirmed Revenue', '', Number(summary.actual_revenue).toFixed(2)],
        ['Escrow (Pending)', '', Number(summary.escrow_amount).toFixed(2)],
        ['Total Expenses', '', Number(summary.total_expenses).toFixed(2)],
        ['Net Profit / Loss', '', Number(summary.net_profit).toFixed(2)],
      ];
      autoTable(doc, {
        startY: 34,
        head: [['Category', '', 'Amount (₹)']],
        body: rows,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [217, 119, 6] },
        columnStyles: { 2: { halign: 'right' } }
      });
      // Expense detail below
      const startY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.text('Expense Breakdown', 14, startY);
      autoTable(doc, {
        startY: startY + 4,
        head: [['Date', 'Item', 'Qty', 'Unit', 'Amount (₹)']],
        body: expenses.map(e => [e.date, e.item_name, e.quantity, e.unit, Number(e.amount).toFixed(2)]),
        foot: [['', '', '', 'TOTAL', Number(expTotal).toFixed(2)]],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [156, 163, 175] },
        footStyles: { fillColor: [229, 231, 235], fontStyle: 'bold' }
      });
    }
    doc.save(`hhob-${type}-${periodLabel().replace(/\s/g, '_')}.pdf`);
  };

  // ─── Export Excel ────────────────────────────────────────────────────────────
  const exportExcel = (type) => {
    const wb = XLSX.utils.book_new();

    if (type === 'expenses' || type === 'accounts') {
      // Expenses sheet
      const expData = [
        ['Date', 'Item Name', 'Quantity', 'Unit', 'Amount (₹)', 'Notes'],
        ...expenses.map(e => [e.date, e.item_name, e.quantity, e.unit, e.amount, e.notes || '']),
        ['', '', '', 'TOTAL', expTotal, '']
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(expData);
      XLSX.utils.book_append_sheet(wb, ws1, 'Expenses');
    }

    if (type === 'accounts' && summary) {
      const accData = [
        [`Accounts Summary — ${periodLabel()}`],
        [],
        ['Category', 'Amount (₹)'],
        ['Confirmed Revenue', summary.actual_revenue],
        ['Escrow (Pending Orders)', summary.escrow_amount],
        ['Total Revenue (incl. escrow)', summary.total_revenue_incl_escrow],
        [],
        ['Total Expenses', summary.total_expenses],
        [],
        ['Net Profit / Loss', summary.net_profit],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(accData);
      XLSX.utils.book_append_sheet(wb, ws2, 'Summary');
    }

    XLSX.writeFile(wb, `hhob-accounts-${periodLabel().replace(/\s/g, '_')}.xlsx`);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  const profitColor = summary?.net_profit >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="space-y-5">

      {/* ── Add Expense Form ── */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-accent mb-4 flex items-center gap-2">
          <Plus className="h-4 w-4 text-primary" /> Add Procurement Expense
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">

          {/* Item name with autocomplete */}
          <div className="col-span-2 relative" ref={suggRef}>
            <Label className="text-xs text-muted-foreground mb-1 block">Item Name *</Label>
            <Input
              value={form.item_name}
              onChange={e => handleItemNameChange(e.target.value)}
              onFocus={() => form.item_name && fetchSuggestions(form.item_name)}
              placeholder="e.g. All-purpose flour"
              className="h-9 text-sm"
              data-testid="expense-item-name"
            />
            {showSuggestions && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <div
                    key={i}
                    className="px-3 py-2 text-sm hover:bg-amber-50 cursor-pointer"
                    onMouseDown={() => selectSuggestion(s)}
                  >
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Quantity *</Label>
            <Input
              type="number" min="0" step="0.01"
              value={form.quantity}
              onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
              placeholder="e.g. 500"
              className="h-9 text-sm"
              data-testid="expense-quantity"
            />
          </div>

          {/* Unit */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Unit *</Label>
            <Select value={form.unit} onValueChange={v => setForm(p => ({ ...p, unit: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid="expense-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Amount (₹) *</Label>
            <Input
              type="number" min="0" step="0.01"
              value={form.amount}
              onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              placeholder="e.g. 120"
              className="h-9 text-sm"
              data-testid="expense-amount"
            />
          </div>

          {/* Date */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Date *</Label>
            <Input
              type="date" value={form.date} max={TODAY}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className="h-9 text-sm"
              data-testid="expense-date"
            />
          </div>

          {/* Notes - full width on small screens */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-4">
            <Label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</Label>
            <Input
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="e.g. Bought from BigBasket"
              className="h-9 text-sm"
              data-testid="expense-notes"
            />
          </div>

          {/* Submit */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-2 flex items-end">
            <Button
              onClick={handleAddExpense} disabled={saving}
              className="w-full h-9 gap-2"
              data-testid="expense-add-btn"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add Expense
            </Button>
          </div>
        </div>
      </Card>

      {/* ── Period Selector ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <PeriodSelector period={period} setPeriod={setPeriod} custom={custom} setCustom={setCustom} monthYear={monthYear} setMonthYear={setMonthYear} />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-8 gap-1.5 text-xs">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Revenue vs Expenses Summary ── */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 border-l-4 border-l-green-500">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-green-500" /> Confirmed Revenue
            </p>
            <p className="text-lg font-bold text-green-600" data-testid="confirmed-revenue">{fmt(summary.actual_revenue)}</p>
            <p className="text-xs text-muted-foreground">{summary.confirmed_orders_count} orders</p>
          </Card>

          <Card className="p-3 border-l-4 border-l-amber-400">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3 text-amber-500" /> Escrow (Pending)
            </p>
            <p className="text-lg font-bold text-amber-600" data-testid="escrow-revenue">{fmt(summary.escrow_amount)}</p>
            <p className="text-xs text-muted-foreground">{summary.escrow_orders_count} pending orders</p>
          </Card>

          <Card className="p-3 border-l-4 border-l-red-400">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <TrendingDown className="h-3 w-3 text-red-500" /> Total Expenses
            </p>
            <p className="text-lg font-bold text-red-600" data-testid="total-expenses">{fmt(summary.total_expenses)}</p>
            <p className="text-xs text-muted-foreground">{summary.expenses_count} entries</p>
          </Card>

          <Card className="p-3 border-l-4 border-l-blue-500">
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <IndianRupee className="h-3 w-3 text-blue-500" /> Net P&L
            </p>
            <p className={`text-lg font-bold ${profitColor}`} data-testid="net-profit">{fmt(summary.net_profit)}</p>
            <p className="text-xs text-muted-foreground">Revenue − Expenses</p>
          </Card>
        </div>
      )}

      {/* ── Export Buttons ── */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => exportPDF('expenses')} data-testid="export-expenses-pdf">
          <Download className="h-3.5 w-3.5" /> Expenses PDF
        </Button>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => exportExcel('expenses')} data-testid="export-expenses-excel">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Expenses Excel
        </Button>
        <Button size="sm" className="h-8 gap-1.5 text-xs bg-amber-700 hover:bg-amber-800" onClick={() => exportPDF('accounts')} data-testid="export-accounts-pdf">
          <Download className="h-3.5 w-3.5" /> Accounts PDF
        </Button>
        <Button size="sm" className="h-8 gap-1.5 text-xs bg-green-700 hover:bg-green-800" onClick={() => exportExcel('accounts')} data-testid="export-accounts-excel">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Accounts Excel
        </Button>
      </div>

      {/* ── Expenses Table ── */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-accent flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            Expenses — {periodLabel()}
          </h3>
          <Badge variant="secondary" className="text-xs font-mono">
            Total: {fmt(expTotal)}
          </Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : expenses.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No expenses found for this period. Add your first entry above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left py-2 pr-3">Item</th>
                  <th className="text-right py-2 pr-3">Qty</th>
                  <th className="text-left py-2 pr-3">Unit</th>
                  <th className="text-right py-2 pr-3">Amount</th>
                  <th className="text-left py-2 pr-3 hidden sm:table-cell">Notes</th>
                  <th className="py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50 group">
                    <td className="py-2 pr-3 text-muted-foreground text-xs whitespace-nowrap">{e.date}</td>
                    <td className="py-2 pr-3 font-medium">{e.item_name}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{e.quantity}</td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className="text-xs px-1.5 py-0">{e.unit}</Badge>
                    </td>
                    <td className="py-2 pr-3 text-right font-medium tabular-nums text-red-600">
                      {fmt(e.amount)}
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground hidden sm:table-cell truncate max-w-[140px]">
                      {e.notes || '—'}
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => handleDelete(e.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                        data-testid={`delete-expense-${e.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-amber-50">
                  <td colSpan={4} className="py-2 pr-3 text-xs font-semibold text-right">Total</td>
                  <td className="py-2 pr-3 text-right font-bold text-red-700 tabular-nums">
                    {fmt(expTotal)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminExpenses;
